import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ContextBar } from "../components/ContextBar";
import { ROUTES } from "../lib/routes";
import {
  ACADEMIC_YEAR,
  PUBLISHED_ON,
  TERMS,
  HOLIDAYS_FULL,
  WORKING_SATURDAYS,
  CALENDAR_PDF,
} from "../lib/academicCalendar";
import { docUrl, formatBytes } from "../lib/docUrl";
import { useScrollPastHero } from "../lib/useScrollPastHero";

export default function AcademicCalendar() {
  const [heroRef, scrolledPast] = useScrollPastHero<HTMLElement>();
  const termsWithDays = TERMS.filter((t) => t.workingDays !== undefined);
  const totalDays = termsWithDays.reduce((s, t) => s + (t.workingDays ?? 0), 0);

  const [activeTerm, setActiveTerm] = useState(0);
  const termTabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const term = TERMS[activeTerm];

  // Roving-focus keyboard nav across the tab strip (WAI-ARIA tabs pattern) —
  // same behaviour as the category filter on /events.
  function onTermTabKeyDown(e: React.KeyboardEvent, i: number) {
    const delta = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (!delta && e.key !== "Home" && e.key !== "End") return;
    e.preventDefault();
    const next =
      e.key === "Home" ? 0 : e.key === "End" ? TERMS.length - 1 : (i + delta + TERMS.length) % TERMS.length;
    setActiveTerm(next);
    termTabRefs.current[next]?.focus();
  }

  return (
    <article>
      <ContextBar
        visible={scrolledPast}
        tag={`AY ${ACADEMIC_YEAR}`}
        title="Academic Calendar"
        back={{ label: "Academics", to: ROUTES.academics }}
      />

      <section className="center-hero" ref={heroRef}>
        <div className="container">
          <nav className="event-crumbs" aria-label="Breadcrumb">
            <Link to={ROUTES.home}>Home</Link>
            <span aria-hidden>/</span>
            <Link to={ROUTES.academics}>Academics</Link>
            <span aria-hidden>/</span>
            <span>Calendar</span>
          </nav>
          <div className="ch-eyebrow">Published · {PUBLISHED_ON}</div>
          <span className="ch-short">AY {ACADEMIC_YEAR}</span>
          <h1 className="ch-name">Academic Calendar.</h1>
          <p className="ch-summary">
            Term dates, internal-assessment windows and end-semester examinations for AY {ACADEMIC_YEAR}, as circulated by the Controller of Examinations.
          </p>

          <div className="ch-stats">
            {termsWithDays.length > 0 && (
              <div>
                <div className="num">{totalDays}</div>
                <div className="lbl">Working days</div>
              </div>
            )}
            <div>
              <div className="num">{TERMS.length}</div>
              <div className="lbl">Semesters</div>
            </div>
            {HOLIDAYS_FULL.length > 0 && (
              <div>
                <div className="num">{HOLIDAYS_FULL.length}</div>
                <div className="lbl">Holidays</div>
              </div>
            )}
            {WORKING_SATURDAYS.length > 0 && (
              <div>
                <div className="num">{WORKING_SATURDAYS.length}</div>
                <div className="lbl">Working Saturdays</div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="detail-shell">
        <div className="container">
          <div className="detail-shell-grid">
            <div className="detail-body">
              {TERMS.length > 1 && (
                <div className="filter-tabs" role="tablist" aria-label="Select semester" style={{ marginBottom: "var(--space-6)" }}>
                  {TERMS.map((t, i) => (
                    <button
                      key={t.short}
                      ref={(el) => { termTabRefs.current[i] = el; }}
                      role="tab"
                      type="button"
                      id={`term-tab-${i}`}
                      aria-selected={activeTerm === i}
                      aria-controls="term-panel"
                      tabIndex={activeTerm === i ? 0 : -1}
                      className={"filter-tab" + (activeTerm === i ? " is-active" : "")}
                      onClick={() => setActiveTerm(i)}
                      onKeyDown={(e) => onTermTabKeyDown(e, i)}
                    >
                      {t.short}
                    </button>
                  ))}
                </div>
              )}

              {term && (
                <div id="term-panel" role="tabpanel" aria-labelledby={`term-tab-${activeTerm}`} style={{ marginBottom: "var(--space-7)" }}>
                  <div className="kicker" style={{ marginBottom: 6 }}>{term.short}</div>
                  <h2 style={{ marginTop: 0 }}>{term.name}</h2>
                  <p style={{ color: "var(--text-soft)", maxWidth: "60ch" }}>
                    <b>Span:</b> {term.span}
                    {term.workingDays !== undefined && (
                      <>
                        <br />
                        <b>Working days:</b> {term.workingDays}
                      </>
                    )}
                  </p>
                  <ol className="event-schedule">
                    {term.events.map((e, i) => (
                      <li key={i}>
                        <span className="es-time">{e.date}</span>
                        <span className="es-line" aria-hidden />
                        <span className="es-body">
                          <span className="es-what">{e.title}</span>
                          <span className="es-who">
                            {e.kind.toUpperCase()}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {HOLIDAYS_FULL.length > 0 && (
                <>
                  <h2>Holidays</h2>
                  <ul className="detail-list">
                    {HOLIDAYS_FULL.map((h, i) => (
                      <li key={i}><b>{h.date}</b> · {h.title}</li>
                    ))}
                  </ul>
                </>
              )}

              {WORKING_SATURDAYS.length > 0 && (
                <>
                  <h2>Working Saturdays</h2>
                  <ul className="detail-list">
                    {WORKING_SATURDAYS.map((row, i) => (
                      <li key={i}>{row}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            <aside className="detail-meta-card is-accented">
              <div className="dmc-block">
                <div className="dmc-label">Academic year</div>
                <div className="dmc-value">{ACADEMIC_YEAR}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Published</div>
                <div className="dmc-value">{PUBLISHED_ON}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Authority</div>
                <div className="dmc-value">Dean (Academics)</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Examinations</div>
                <div className="dmc-value">
                  <Link to={ROUTES.examinations}>Visit COE office →</Link>
                </div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Curriculum</div>
                <div className="dmc-value">
                  <Link to={ROUTES.curriculum}>Curriculum & Syllabus →</Link>
                </div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Calendar PDF</div>
                <div className="dmc-value">
                  <a
                    href={docUrl(CALENDAR_PDF.url)}
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label={`Download the ${CALENDAR_PDF.name} (PDF)`}
                  >
                    Download ({formatBytes(CALENDAR_PDF.bytes)}) →
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </article>
  );
}
